import React, { useCallback, useId } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { UncontrolledTooltip } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWandMagicSparkles } from '@fortawesome/pro-light-svg-icons/faWandMagicSparkles';
import { TypedMemo } from '../../../common/types';
import { StudioActionError } from '../studio/StudioActionError';
import { StudioActionFeedback } from '../studio/studioActionErrorText';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Promote pillar; each part uses its own slice of it */
import styles from '../Promote.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ComposerCopyToolbarProps {
  used: number;
  /** Null on the master copy, which has no ceiling of its own. */
  limit: number | null;
  /** Why the AI buttons cannot run now (one sentence), or undefined when they can. */
  aiBlockReason?: string;
  isWriting: boolean;
  /** The result of the last failed AI write, shown under the AI buttons until the next action. */
  aiError?: StudioActionFeedback | null;
  onWriteMaster: () => void;
  onTailor: () => void;
  /** Offered only on a channel tab that carries copy of its own. */
  onReset?: () => void;
}

interface AiChipProps {
  label: string;
  blockReason?: string;
  hintId: string;
  onClick: () => void;
}

/**
 * One AI button. A blocked button stays in the tab order (`aria-disabled`, not `disabled`), so a
 * keyboard user reaches its tooltip, and the hint line below says the same reason.
 */
const AiChipComponent: React.FC<AiChipProps> = ({ label, blockReason, hintId, onClick }) => {
  // A CSS-safe id: reactstrap finds the target with a selector.
  const targetId = `composer-ai-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const isBlocked = blockReason !== undefined;

  const click = useCallback(() => {
    if (!isBlocked) {
      onClick();
    }
  }, [isBlocked, onClick]);

  return (
    <>
      <button
        id={targetId}
        type="button"
        className={styles.aiChip}
        aria-disabled={isBlocked || undefined}
        aria-describedby={isBlocked ? hintId : undefined}
        onClick={click}
      >
        <FontAwesomeIcon icon={faWandMagicSparkles} />
        {label}
      </button>
      {isBlocked ? (
        <UncontrolledTooltip target={targetId} trigger="hover focus" placement="top">
          {blockReason}
        </UncontrolledTooltip>
      ) : null}
    </>
  );
};

const AiChip = TypedMemo(AiChipComponent);

/** What the copy box is worked on with, and how long the copy has become. */
const ComposerCopyToolbarComponent: React.FC<ComposerCopyToolbarProps> = ({
  used,
  limit,
  aiBlockReason,
  isWriting,
  aiError = null,
  onWriteMaster,
  onTailor,
  onReset,
}) => {
  const intl = useIntl();
  const hintId = `composer-ai-hint-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const isOverLimit = limit !== null && used > limit;

  return (
    <div className={styles.stack}>
      <div className={styles.copyToolbar}>
        <div className={styles.copyToolbarLeft} aria-busy={isWriting || undefined}>
          <AiChip
            label={intl.formatMessage({ id: 'app.sml.promote.composer.ai.write', defaultMessage: 'Write with AI' })}
            blockReason={aiBlockReason}
            hintId={hintId}
            onClick={onWriteMaster}
          />
          <AiChip
            label={intl.formatMessage({
              id: 'app.sml.promote.composer.ai.tailor',
              defaultMessage: 'Tailor per platform',
            })}
            blockReason={aiBlockReason}
            hintId={hintId}
            onClick={onTailor}
          />
          {onReset !== undefined ? (
            <button type="button" className={styles.quietChip} onClick={onReset}>
              {intl.formatMessage({ id: 'app.sml.promote.composer.reset', defaultMessage: 'Reset to master' })}
            </button>
          ) : null}
        </div>
        <div className={clsx(styles.counter, isOverLimit && styles.counterOver)}>
          {limit === null
            ? intl.formatMessage(
                { id: 'app.sml.promote.composer.masterCount', defaultMessage: '{used} characters' },
                { used }
              )
            : intl.formatMessage(
                { id: 'app.sml.promote.post.counter', defaultMessage: '{used} / {limit} characters' },
                { used, limit }
              )}
        </div>
      </div>
      {aiBlockReason !== undefined ? (
        <p id={hintId} className={styles.aiHint} data-testid="composer-ai-hint">
          {aiBlockReason}
        </p>
      ) : null}
      <StudioActionError feedback={aiError} />
    </div>
  );
};

export const ComposerCopyToolbar = TypedMemo(ComposerCopyToolbarComponent);
